import { z } from "zod";
import { isValidPhoneNumber } from "libphonenumber-js";
// CLEANUP: Standardized import path to match accountRecovery.ts
import { VALIDATION_RULES } from "@monorepo/config";
// REFACTOR: Use shared patterns to ensure Signin, Signup, and Strength Meter use identical logic
import { PASSWORD_PATTERNS } from "../shared/password-strength";

// ----------------------------------------------------------------------
// Constants & Messages
// ----------------------------------------------------------------------

export const SIGNUP_ERROR_MESSAGES = {
  REQUIRED: "This field is required",
  EMAIL_INVALID: "Please enter a valid email address",
  EMAIL_MAX: `Email must be less than ${VALIDATION_RULES.EMAIL_MAX_LENGTH} characters`,
  PHONE_INVALID: "Please enter a valid phone number (e.g., +91 99999 99999)",
  CONSENT_REQUIRED: "You must agree to receive account-related SMS messages",
  OTP_LENGTH: `Code must be exactly ${VALIDATION_RULES.OTP_LENGTH} digits`,
  OTP_NUMBER: "Code must contain only numbers",
  NAME_MAX: `Name must be less than ${VALIDATION_RULES.NAME_MAX_LENGTH} characters`,
  COMPANY_MAX: `Company name must be less than ${VALIDATION_RULES.COMPANY_MAX_LENGTH} characters`,
  PASSWORD_MIN: `Password must be at least ${VALIDATION_RULES.PASSWORD_MIN_LENGTH} characters`,
  PASSWORD_MAX: `Password must be less than ${VALIDATION_RULES.PASSWORD_MAX_LENGTH} characters`,
  PASSWORD_LOWER: "Password must contain at least one lowercase letter",
  PASSWORD_UPPER: "Password must contain at least one uppercase letter",
  PASSWORD_NUMBER: "Password must contain at least one number",
  PASSWORD_SPECIAL: "Password must contain at least one special character",
  PASSWORD_MISMATCH: "Passwords don't match",
  BACKUP_CODE_INVALID: "Invalid backup code format",
} as const;

// ----------------------------------------------------------------------
// Field Schemas
// ----------------------------------------------------------------------

/**
 * Email Validation Schema
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, SIGNUP_ERROR_MESSAGES.REQUIRED)
  .email(SIGNUP_ERROR_MESSAGES.EMAIL_INVALID)
  .max(VALIDATION_RULES.EMAIL_MAX_LENGTH, SIGNUP_ERROR_MESSAGES.EMAIL_MAX);

/**
 * OTP Validation Schema
 * Used for Email and Phone verification codes
 */
export const otpSchema = z
  .string()
  .length(VALIDATION_RULES.OTP_LENGTH, SIGNUP_ERROR_MESSAGES.OTP_LENGTH)
  .regex(/^\d+$/, SIGNUP_ERROR_MESSAGES.OTP_NUMBER);

/**
 * TOTP / Authenticator Code Schema
 */
export const totpCodeSchema = otpSchema;

/**
 * Phone Number Validation Schema
 * Validates E.164 format via libphonenumber-js
 */
export const phoneSchema = z
  .string()
  .min(1, SIGNUP_ERROR_MESSAGES.REQUIRED)
  .refine(
    (value) => {
      try {
        return isValidPhoneNumber(value);
      } catch {
        return false;
      }
    },
    {
      message: SIGNUP_ERROR_MESSAGES.PHONE_INVALID,
    }
  );

/**
 * Name Validation Schema
 * SECURITY NOTE: We do NOT enforce strict regex (like /^[a-zA-Z]+$/).
 * XSS prevention is handled by the UI rendering layer.
 */
export const nameSchema = z
  .string()
  .min(1, SIGNUP_ERROR_MESSAGES.REQUIRED)
  .max(VALIDATION_RULES.NAME_MAX_LENGTH, SIGNUP_ERROR_MESSAGES.NAME_MAX)
  .trim();

/**
 * Company Name Validation Schema
 */
export const companySchema = z
  .string()
  .min(1, SIGNUP_ERROR_MESSAGES.REQUIRED)
  .max(VALIDATION_RULES.COMPANY_MAX_LENGTH, SIGNUP_ERROR_MESSAGES.COMPANY_MAX)
  .trim();

/**
 * Password Validation Schema
 * Enforces strong password requirements using shared patterns.
 */
export const passwordSchema = z
  .string()
  .min(VALIDATION_RULES.PASSWORD_MIN_LENGTH, SIGNUP_ERROR_MESSAGES.PASSWORD_MIN)
  .max(VALIDATION_RULES.PASSWORD_MAX_LENGTH, SIGNUP_ERROR_MESSAGES.PASSWORD_MAX)
  // REFACTOR: Use shared patterns for consistency
  .refine((val) => PASSWORD_PATTERNS.LOWER.test(val), {
    message: SIGNUP_ERROR_MESSAGES.PASSWORD_LOWER,
  })
  .refine((val) => PASSWORD_PATTERNS.UPPER.test(val), {
    message: SIGNUP_ERROR_MESSAGES.PASSWORD_UPPER,
  })
  .refine((val) => PASSWORD_PATTERNS.NUMBER.test(val), {
    message: SIGNUP_ERROR_MESSAGES.PASSWORD_NUMBER,
  })
  .refine((val) => PASSWORD_PATTERNS.SPECIAL.test(val), {
    message: SIGNUP_ERROR_MESSAGES.PASSWORD_SPECIAL,
  });

/**
 * Password Confirmation Schema
 */
export const passwordConfirmSchema = z
  .string()
  .min(1, "Please confirm your password");

/**
 * Backup Code Validation Schema
 */
export const backupCodeSchema = z
  .string()
  .min(1, SIGNUP_ERROR_MESSAGES.REQUIRED)
  .regex(/^[A-Z0-9-]+$/i, SIGNUP_ERROR_MESSAGES.BACKUP_CODE_INVALID);

// ==========================================
// FORM SCHEMAS (Steps)
// ==========================================

/**
 * Step 1: Email Identify Form Schema
 */
export const identifyFormSchema = z.object({
  email: emailSchema,
});

export type IdentifyFormData = z.infer<typeof identifyFormSchema>;

/**
 * Step 2: Verify Email OTP Form Schema
 */
export const verifyEmailOtpFormSchema = z.object({
  otp: otpSchema,
});

export type VerifyEmailOtpFormData = z.infer<typeof verifyEmailOtpFormSchema>;

/**
 * Step 3: Profile Entry Form Schema
 */
export const profileFormSchema = z.object({
  fullName: nameSchema,
  companyName: companySchema,
  phone: phoneSchema,
  smsConsent: z.literal(true, {
    errorMap: () => ({ message: SIGNUP_ERROR_MESSAGES.CONSENT_REQUIRED }),
  }),
});

export type ProfileFormData = z.infer<typeof profileFormSchema>;

/**
 * Step 4: Verify Phone OTP Form Schema
 */
export const verifyPhoneOtpFormSchema = z.object({
  otp: otpSchema,
});

export type VerifyPhoneOtpFormData = z.infer<typeof verifyPhoneOtpFormSchema>;

/**
 * Step 5: Set Password Form Schema
 */
export const setPasswordFormSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: passwordConfirmSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: SIGNUP_ERROR_MESSAGES.PASSWORD_MISMATCH,
    path: ["confirmPassword"],
  });

export type SetPasswordFormData = z.infer<typeof setPasswordFormSchema>;

/**
 * Step 6: TOTP Verification Form Schema
 */
export const totpVerificationFormSchema = z.object({
  code: totpCodeSchema,
});

export type TotpVerificationFormData = z.infer<
  typeof totpVerificationFormSchema
>;

// ==========================================
// AGGREGATE TYPES
// ==========================================

export interface SignupData {
  email: string;
  fullName: string;
  companyName: string;
  phone: string;
  password: string;
  mfaMethod: "totp" | "webauthn" | null;
  totpSecret?: string;
  webauthnCredentialId?: string;
  backupCodes?: string[];
}
